import React, { useEffect, useMemo, useState } from 'react'
import type { CategoryItem } from '../../../../../gateways/breadstack/categoryApis'
import { useDebounce } from '../../../../../hooks/useDebounce'
import { useCategorySearchResults } from '../../../hooks/useCategorySearchResults'

interface Props {
  open: boolean
  categories: CategoryItem[]
  categoriesLoading: boolean
  excludedIds: number[]
  onAdd: (ids: number[]) => Promise<void> | void
  onClose: () => void
  saving: boolean
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: '#5A6B7B',
  marginBottom: 6,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 12px',
  fontSize: 13,
  color: '#2C3E50',
  background: '#fff',
  border: '1px solid #E8ECF0',
  borderRadius: 8,
  outline: 'none',
  fontFamily: 'inherit',
}

export const ExcludedCategoriesDialog: React.FC<Props> = ({
  open,
  categories,
  categoriesLoading,
  excludedIds,
  onAdd,
  onClose,
  saving,
}) => {
  const [query, setQuery] = useState('')
  const [staged, setStaged] = useState<CategoryItem[]>([])
  const debouncedQuery = useDebounce(query, 150)

  useEffect(() => {
    if (open) {
      setQuery('')
      setStaged([])
    }
  }, [open])

  const stagedIds = useMemo(
    () => new Set(staged.map((c) => Number(c.id))),
    [staged],
  )

  const hiddenIds = useMemo(
    () =>
      new Set<number>([...excludedIds, ...staged.map((c) => Number(c.id))]),
    [excludedIds, staged],
  )
  const results = useCategorySearchResults({
    categories,
    query: debouncedQuery,
    hiddenIds,
  })

  if (!open) return null

  const addStaged = (cat: CategoryItem) => {
    if (stagedIds.has(Number(cat.id))) return
    setStaged((prev) => [...prev, cat])
    setQuery('')
  }

  const removeStaged = (id: string) => {
    setStaged((prev) => prev.filter((c) => c.id !== id))
  }

  const canSave = staged.length > 0 && !saving

  const handleSubmit = async () => {
    if (!canSave) return
    await onAdd(staged.map((c) => Number(c.id)))
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
        }}
      />
      <div
        style={{
          position: 'relative',
          background: '#fff',
          borderRadius: 12,
          width: 480,
          maxWidth: 'calc(100vw - 32px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          overflow: 'visible',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 20px',
            borderBottom: '1px solid #E8ECF0',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          <h3
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: '#2C3E50',
              margin: 0,
            }}
          >
            Exclude Categories
          </h3>
          <button
            type="button"
            onClick={saving ? undefined : onClose}
            style={{
              padding: 4,
              background: 'none',
              border: 'none',
              cursor: saving ? 'not-allowed' : 'pointer',
              color: '#7F8C8D',
              opacity: saving ? 0.5 : 1,
            }}
            aria-label="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{ padding: 20 }}>
          <label style={labelStyle} htmlFor="exclude-category-search">
            Pick categories
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="exclude-category-search"
              type="text"
              placeholder="Type a category name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={inputStyle}
              autoComplete="off"
              disabled={saving}
            />
            {query && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: 4,
                  maxHeight: 220,
                  overflowY: 'auto',
                  background: '#fff',
                  border: '1px solid #E8ECF0',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                  zIndex: 2,
                }}
              >
                {categoriesLoading ? (
                  <div
                    style={{
                      padding: '10px 12px',
                      fontSize: 12,
                      color: '#7F8C8D',
                    }}
                  >
                    Loading categories…
                  </div>
                ) : results.length === 0 ? (
                  <div
                    style={{
                      padding: '10px 12px',
                      fontSize: 12,
                      color: '#7F8C8D',
                    }}
                  >
                    No categories match "{query}"
                  </div>
                ) : (
                  results.map(({ cat, isChild }) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => addStaged(cat)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        width: '100%',
                        textAlign: 'left',
                        padding: '8px 12px',
                        paddingLeft: isChild ? 28 : 12,
                        fontSize: 13,
                        fontWeight: isChild ? 400 : 500,
                        color: isChild ? '#5A6B7B' : '#2C3E50',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F1F5F9'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent'
                      }}
                    >
                      {isChild && (
                        <span style={{ color: '#B0BEC5', fontSize: 12 }}>
                          └
                        </span>
                      )}
                      {cat.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {staged.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginTop: 12,
              }}
            >
              {staged.map((c) => (
                <span
                  key={c.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    background: '#DBEAFE',
                    color: '#2563EB',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {c.name}
                  <button
                    type="button"
                    onClick={() => removeStaged(c.id)}
                    aria-label={`Remove ${c.name}`}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#2563EB',
                      fontSize: 14,
                      lineHeight: 1,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            padding: '14px 20px',
            borderTop: '1px solid #E8ECF0',
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              color: '#374151',
              background: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSave}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              background: canSave ? '#2563EB' : '#9CA3AF',
              border: 'none',
              borderRadius: 8,
              cursor: canSave ? 'pointer' : 'not-allowed',
              opacity: canSave ? 1 : 0.7,
            }}
          >
            {saving ? 'Saving…' : 'Add Selected'}
          </button>
        </div>
      </div>
    </div>
  )
}
